import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import croppedLogo from '../assets/soul-sanskar-logo-cropped.png';

const LoginPage = () => {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSuccess = async (credentialResponse) => {
    const success = await loginWithGoogle(credentialResponse.credential);
    if (success) navigate('/dashboard', { replace: true });
  };

  return (
    <div className="grid min-h-screen grid-cols-[minmax(0,1fr)] bg-background lg:grid-cols-[0.95fr_1.05fr]" data-testid="login-page">
      <div
        className="relative hidden lg:flex min-h-screen flex-col bg-[hsl(217_33%_10%)] text-slate-100 p-12 xl:p-16 border-r border-slate-800 overflow-hidden"
        data-testid="login-left-panel"
      >
        <div className="absolute inset-6 border border-white/[0.035]" aria-hidden="true" />
        <div className="absolute inset-y-0 right-[16%] w-px bg-white/[0.045]" aria-hidden="true" />
        <div className="absolute left-0 top-[31%] h-px w-full bg-white/[0.045]" aria-hidden="true" />

        <div className="relative flex items-center gap-3" data-testid="login-left-brand">
          <span className="h-px w-8 bg-[hsl(30_95%_70%)]" aria-hidden="true" />
          <span className="sr-only" data-testid="login-brand-name">Soul Sanskar</span>
          <p className="font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-slate-500" data-testid="login-brand-portal-label">
            Internal team workspace
          </p>
        </div>

        <div className="relative my-auto max-w-md py-10">
          <div
            className="flex aspect-[654/464] w-52 items-center justify-center overflow-hidden rounded-xl border border-white/15 bg-[#fdfcf9] p-4 shadow-[0_28px_80px_-32px_rgba(0,0,0,0.85)] xl:w-56"
            data-testid="login-hero-logo-tile"
          >
            <img
              src={croppedLogo}
              alt="Soul Sanskar"
              className="h-full w-full object-contain"
              data-testid="login-hero-logo-img"
            />
          </div>
          <div
            className="mt-9 inline-flex items-center gap-2 rounded-full border border-slate-700/80 bg-slate-900/80 px-3 py-1.5 text-xs font-medium text-slate-300"
            data-testid="login-team-access-badge"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" aria-hidden="true" />
            Team access only
          </div>
          <h1
            className="font-display mt-5 text-4xl xl:text-5xl font-bold leading-[1.06] text-white"
            data-testid="login-internal-ops-heading"
          >
            Internal Operations
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-400" data-testid="login-internal-ops-description">
            Authorized staff portal
          </p>
        </div>

        <div className="relative flex items-center justify-between border-t border-slate-800/90 pt-6 text-[11px] text-slate-500">
          <p data-testid="login-copyright">© {new Date().getFullYear()} Soul Sanskar</p>
          <div className="flex items-center gap-2 font-mono uppercase" data-testid="login-system-id">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
            SSO ready
          </div>
        </div>
      </div>

      <div className="flex w-full min-w-0 items-center justify-center bg-[hsl(40_20%_98%)] p-2 sm:p-10 lg:p-16">
        <div className="relative w-full min-w-0 max-w-md overflow-hidden rounded-xl border border-slate-200/80 bg-white p-4 shadow-[0_28px_80px_-44px_rgba(15,23,42,0.38)] animate-fade-in sm:p-10">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[hsl(var(--primary))] via-amber-400 to-[hsl(var(--secondary))]" aria-hidden="true" />
          <div className="mx-auto mb-8 aspect-[654/464] w-36 lg:hidden" data-testid="login-mobile-logo-tile">
            <img src={croppedLogo} alt="Soul Sanskar" className="h-full w-full object-contain" data-testid="login-mobile-logo-img" />
          </div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary" data-testid="login-welcome-label">Welcome back</p>
          <h2 className="font-display mt-2 text-3xl font-bold tracking-tight" data-testid="login-sign-in-heading">Sign in to OPS Panel</h2>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="login-sign-in-description">Sign in with your Soul Sanskar Google account.</p>
          <div className="mt-8 min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 p-2 sm:p-5" data-testid="google-login-btn">
            <div className="google-login-fluid mx-auto w-full max-w-[260px] sm:max-w-[320px]">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => toast.error('Google Sign-In failed. Please try again.')}
                size="large"
                width="320"
                text="continue_with"
                shape="pill"
                logo_alignment="left"
              />
            </div>
          </div>
          <div className="mt-5 flex items-center justify-center gap-2 text-[11px] text-slate-400" data-testid="login-security-note">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Protected workspace · Google SSO
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
